import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import ModalWrapper from './modalWrapper/basicModalWrapper';
import ModuleExplorer from '../explorer/moduleExplorer';
import CustomSnackbar from '../snackbar/CustomSnackbar';
import LabelChip from "../chips/LabelChip";
import { fetchModulesWithCount } from '../../utils/api';
import FolderDrawerTreeView from '../FolderDrawerTreeView';

export function CloneOrMoveModal({ open, onClose, onConfirm, action, currentModuleId, selectedCases }) {
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictingCases, setConflictingCases] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open) {
      setSelectedModuleId(null);
      setConflictingCases([]);
    }
  }, [open]);

  useEffect(() => {
    (async ()=>{
      const data = await fetchModulesWithCount();
      if(data?.length) {
        setTreeData([...data]);
      }
    })()
  }, [open])

  const handleModuleSelect = (moduleId) => {
    setSelectedModuleId(moduleId);
    // Clear any previous conflicts when selecting a new module
    setConflictingCases([]);
  };

  const handleConfirm = () => {
    if (selectedModuleId) {
        //normalizedCases was needed as the testcase api for module does not return us module data in the response hence here to check which module the testcase is coming from incase of a selected module we are adding module to the selected testcases and adding the currentModule id to it.
        const normalizedCases = selectedCases.map(testCase => ({
            ...testCase,
            module: testCase.module || { id: currentModuleId, name: 'Unknown' }
        }));
        
        const conflictCases = normalizedCases.filter(testCase => 
            testCase.module.id === selectedModuleId
        );


        if (conflictCases.length > 0) {
            setConflictingCases(conflictCases);
            handleSnackbar('same-module-error');
            return;
        }
        
        onConfirm(selectedModuleId);
        onClose();
    }
  };


  const handleSnackbar = (actionType) => {
    let message = '';
    let severity = 'success'; 

    switch (actionType) {
      case 'same-module-error':
        message =  `Cannot ${action} test cases to their source module`;
        severity = 'error';
        break;
      default:
        message = 'Action completed successfully!';
        severity = 'info';
    }

    setMessage(message);
    setSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const buttons = [
    { 
      label: 'Cancel', 
      onClick: onClose, 
      variant: 'outline', 
      className: 'primary',
    },
    {
      label: action === 'clone' ? 'Clone' : 'Move',
      onClick: handleConfirm,
      variant: 'primary',
      className: 'white bg-blue600',
      disabled: !selectedModuleId
    }
  ];

  // If there are conflicting cases, show them in a list
  const ConflictingCasesList = () => (
    
    conflictingCases.length > 0 && (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography color="red" sx={{ mb: 1 }}>
          The following 
          <LabelChip 
            label={`${conflictingCases.length}`}  // Use LabelChip to show the count
            sx={{ backgroundColor: '#f2f2f2', color: '#D32F2F', fontWeight: 'bold' }} 
          />
          test case(s) cannot be {action}d (same source and target module):
        </Typography>
        <List dense sx={{ 
          bgcolor: '#f2f2f2',
          border: '1px solid #ffcdd2',
          borderRadius: 1,
          maxHeight: '150px',
          overflow: 'auto'
        }}>
          {conflictingCases.map((testCase) => (
            <ListItem key={testCase.id} sx={{ py: 0.5 }}>
              <ListItemText 
                primary={`${testCase.key || 'Unknown'} - ${testCase.title || 'Unknown'}`}
                secondary={`Current module: ${testCase.module?.name || 'Unknown'}`}
                primaryTypographyProps={{ 
                  color: 'red'
                }}
                secondaryTypographyProps={{ 
                }}
              />
            </ListItem>
          ))}
        </List>
        <Typography color="red"  sx={{ mb: 1, mt: 1 }}>
          Please resolve the conflicts before trying to {action} again.
        </Typography>
      </Box>
    )
  );

  return (
    <div>
      <ModalWrapper 
        open={open} 
        onClose={onClose} 
        title={`${action === 'clone' ? 'Clone' : 'Move'} Test Case${action === 'move' ? 's' : ''}`}
        buttons={buttons}
        fields={[]}
      >
        <ConflictingCasesList />
        <Box sx={{ flex: 1, minHeight: 350, maxHeight: 350, overflow: 'auto', mt: 2 }}>
          <FolderDrawerTreeView 
            data={treeData} 
            onLeafNodeClick={handleModuleSelect}
            hideHeader={true}
            showSearch={true}
            smallView={true}
            selected={selected}
            setSelected={setSelected}
          />
        </Box>

      </ModalWrapper>
      <CustomSnackbar
        open={snackbarOpen}
        message={message}
        severity={severity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
}